import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "app-notification-panel" }
const _hoisted_2 = { class: "app-notification-panel__header" }
const _hoisted_3 = { class: "app-notification-panel__header-content" }
const _hoisted_4 = { class: "app-notification-panel__title-section" }
const _hoisted_5 = { class: "app-notification-panel__title" }
const _hoisted_6 = {
  key: 0,
  class: "app-notification-panel__count-badge"
}
const _hoisted_7 = { class: "app-notification-panel__header-actions" }
const _hoisted_8 = ["title", "disabled"]
const _hoisted_9 = ["title"]
const _hoisted_10 = { class: "app-notification-panel__content" }
const _hoisted_11 = {
  key: 0,
  class: "app-notification-panel__loading"
}
const _hoisted_12 = { class: "app-notification-panel__loading-content" }
const _hoisted_13 = { class: "app-notification-panel__loading-text" }
const _hoisted_14 = {
  key: 1,
  class: "app-notification-panel__error"
}
const _hoisted_15 = { class: "app-notification-panel__error-content" }
const _hoisted_16 = { class: "app-notification-panel__error-title" }
const _hoisted_17 = { class: "app-notification-panel__error-message" }
const _hoisted_18 = {
  key: 2,
  class: "app-notification-panel__empty"
}
const _hoisted_19 = { class: "app-notification-panel__empty-content" }
const _hoisted_20 = { class: "app-notification-panel__empty-title" }
const _hoisted_21 = { class: "app-notification-panel__empty-description" }
const _hoisted_22 = {
  key: 3,
  class: "app-notification-panel__list"
}
const _hoisted_23 = ["onClick"]
const _hoisted_24 = { class: "app-notification-panel__icon" }
const _hoisted_25 = {
  key: 0,
  class: "app-notification-panel__unread-indicator"
}
const _hoisted_26 = { class: "app-notification-panel__content-wrapper" }
const _hoisted_27 = { class: "app-notification-panel__header-row" }
const _hoisted_28 = { class: "app-notification-panel__item-title" }
const _hoisted_29 = { class: "app-notification-panel__item-time" }
const _hoisted_30 = { class: "app-notification-panel__item-message" }
const _hoisted_31 = { class: "app-notification-panel__actions" }
const _hoisted_32 = ["onClick", "title"]
const _hoisted_33 = ["onClick", "title"]

import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import type { UserNotificationDTO } from '@/types'
import { useUserNotifications } from '@/composables/useUserNotifications'
import userNotificationService from '@/services/userNotification.service'
import { showApiSuccessNotification, showApiErrorNotification } from '@/utils/globalNotifications'
import { useI18n } from 'vue-i18n'


export default /*@__PURE__*/_defineComponent({
  __name: 'NotificationPanel',
  props: {
    notifications: {},
    loading: { type: Boolean },
    error: {}
  },
  emits: ["close", "refresh"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const router = useRouter()
const { t } = useI18n()
const {
  getNotificationTitle,
  getNotificationMessage,
  getNotificationActionUrl,
  formatNotificationTime,
  getNotificationIcon,
  getNotificationVariant
} = useUserNotifications()

const unreadCount = computed(() => {
  return props.notifications.filter((n: UserNotificationDTO) => !n.read).length
})

/**
 * Navigate to the notification's target page and mark as read
 */
const handleNotificationClick = async (notif: UserNotificationDTO) => {
  try {
    // Mark as read
    if (!notif.read) {
      await userNotificationService.markAsRead(notif.id)
    }
    
    // Navigate to action URL
    const url = getNotificationActionUrl(notif)
    if (url) {
      await router.push(url)
      emit('close')
    }
    
    // Refresh the notification list
    emit('refresh')
  } catch (error) {
    console.error('Error handling notification click:', error)
    showApiErrorNotification({
      type: 'ERROR',
      message: t('notifications.errors.navigation'),
      code: 'NAVIGATION_ERROR',
      timestamp: new Date().toISOString()
    })
  }
}

/**
 * Mark notification as read without navigating
 */
const handleMarkAsReadOnly = async (notif: UserNotificationDTO, event: Event) => {
  event.stopPropagation() // Prevent navigation
  
  try {
    await userNotificationService.markAsRead(notif.id)
    showApiSuccessNotification(t('notifications.markedAsRead'))
    emit('refresh')
  } catch (error) {
    console.error('Error marking notification as read:', error)
    showApiErrorNotification({
      type: 'ERROR',
      message: t('notifications.errors.markAsRead'),
      code: 'MARK_READ_ERROR',
      timestamp: new Date().toISOString()
    })
  }
}

/**
 * Delete notification
 */
const handleDeleteNotification = async (notif: UserNotificationDTO, event: Event) => {
  event.stopPropagation() // Prevent navigation
  
  try {
    await userNotificationService.deleteNotification(notif.id)
    showApiSuccessNotification(t('notifications.deleted'))
    emit('refresh')
  } catch (error) {
    console.error('Error deleting notification:', error)
    showApiErrorNotification({
      type: 'ERROR',
      message: t('notifications.errors.delete'),
      code: 'DELETE_ERROR',
      timestamp: new Date().toISOString()
    })
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("h3", _hoisted_5, _toDisplayString(_ctx.$t('notifications.title')), 1),
          (unreadCount.value > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(unreadCount.value), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("button", {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('refresh'))),
            class: "app-notification-panel__action-btn app-notification-panel__action-btn--refresh",
            title: _ctx.$t('notifications.refresh'),
            disabled: _ctx.loading
          }, [
            (_openBlock(), _createElementBlock("svg", {
              class: _normalizeClass(["app-notification-panel__refresh-icon", { 'app-notification-panel__refresh-icon--rotating': _ctx.loading }]),
              viewBox: "0 0 24 24",
              width: "18",
              height: "18"
            }, [...(_cache[3] || (_cache[3] = [
              _createElementVNode("path", {
                fill: "currentColor",
                d: "M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
              }, null, -1)
            ]))], 2))
          ], 8, _hoisted_8),
          _createElementVNode("button", {
            onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('close'))),
            class: "app-notification-panel__action-btn app-notification-panel__action-btn--close",
            title: _ctx.$t('common.close')
          }, [...(_cache[4] || (_cache[4] = [
            _createElementVNode("svg", {
              viewBox: "0 0 24 24",
              width: "18",
              height: "18"
            }, [
              _createElementVNode("path", {
                fill: "currentColor",
                d: "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
              })
            ], -1)
          ]))], 8, _hoisted_9)
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_10, [
      (_ctx.loading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            _createElementVNode("div", _hoisted_12, [
              _cache[5] || (_cache[5] = _createElementVNode("div", { class: "app-notification-panel__loading-spinner" }, [
                _createElementVNode("div", { class: "app-notification-panel__spinner-ring" }),
                _createElementVNode("div", { class: "app-notification-panel__spinner-ring" }),
                _createElementVNode("div", { class: "app-notification-panel__spinner-ring" })
              ], -1)),
              _createElementVNode("p", _hoisted_13, _toDisplayString(_ctx.$t('notifications.loading')), 1)
            ])
          ]))
        : (_ctx.error)
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createElementVNode("div", _hoisted_15, [
                _cache[7] || (_cache[7] = _createElementVNode("div", { class: "app-notification-panel__error-icon" }, [
                  _createElementVNode("svg", {
                    viewBox: "0 0 24 24",
                    width: "56",
                    height: "56"
                  }, [
                    _createElementVNode("path", {
                      fill: "currentColor",
                      d: "M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z"
                    })
                  ])
                ], -1)),
                _createElementVNode("h4", _hoisted_16, _toDisplayString(_ctx.$t('notifications.errorTitle')), 1),
                _createElementVNode("p", _hoisted_17, _toDisplayString(_ctx.error), 1),
                _createElementVNode("button", {
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('refresh'))),
                  class: "app-button app-button--secondary app-button--small"
                }, [
                  _cache[6] || (_cache[6] = _createElementVNode("svg", {
                    viewBox: "0 0 24 24",
                    width: "18",
                    height: "18"
                  }, [
                    _createElementVNode("path", {
                      fill: "currentColor",
                      d: "M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
                    })
                  ], -1)),
                  _createTextVNode(" " + _toDisplayString(_ctx.$t('notifications.retry')), 1)
                ])
              ])
            ]))
          : (_ctx.notifications.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                _createElementVNode("div", _hoisted_19, [
                  _cache[8] || (_cache[8] = _createElementVNode("div", { class: "app-notification-panel__empty-icon" }, [
                    _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      width: "72",
                      height: "72"
                    }, [
                      _createElementVNode("path", {
                        fill: "currentColor",
                        d: "M12,2C13.1,2 14,2.9 14,4C14,4.1 14,4.2 13.9,4.3C16.3,5.2 18,7.5 18,10.1V15.5C18,16.8 18.6,18 19.6,18.7L20,19V20H4V19L4.4,18.7C5.4,18 6,16.8 6,15.5V10.1C6,7.5 7.7,5.2 10.1,4.3C10,4.2 10,4.1 10,4C10,2.9 10.9,2 12,2M10,21C10,22.1 10.9,23 12,23C13.1,23 14,22.1 14,21"
                      })
                    ]),
                    _createElementVNode("div", { class: "app-notification-panel__empty-decoration" })
                  ], -1)),
                  _createElementVNode("h4", _hoisted_20, _toDisplayString(_ctx.$t('notifications.noNotifications')), 1),
                  _createElementVNode("p", _hoisted_21, _toDisplayString(_ctx.$t('notifications.noNotificationsDescription')), 1)
                ])
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_22, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.notifications, (notif, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: notif.id,
                    class: _normalizeClass(["app-notification-panel__item", { 'app-notification-panel__item--unread': !notif.read }]),
                    style: _normalizeStyle({ animationDelay: `${index * 50}ms` })
                  }, [
                    _createElementVNode("div", {
                      class: "app-notification-panel__item-content",
                      onClick: ($event: any) => (handleNotificationClick(notif))
                    }, [
                      _createElementVNode("div", _hoisted_24, [
                        _createElementVNode("div", {
                          class: _normalizeClass(["app-notification-panel__icon-bg", `app-notification-panel__icon-bg--${_unref(getNotificationVariant)(notif)}`])
                        }, [
                          _createElementVNode("i", {
                            class: _normalizeClass([`fas ${_unref(getNotificationIcon)(notif)}`, "app-notification-panel__icon-svg"])
                          }, null, 2)
                        ], 2),
                        (!notif.read)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_25))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", _hoisted_26, [
                        _createElementVNode("div", _hoisted_27, [
                          _createElementVNode("h5", _hoisted_28, _toDisplayString(_unref(getNotificationTitle)(notif)), 1),
                          _createElementVNode("span", _hoisted_29, _toDisplayString(_unref(formatNotificationTime)(notif.createdAt)), 1)
                        ]),
                        _createElementVNode("p", _hoisted_30, _toDisplayString(_unref(getNotificationMessage)(notif)), 1)
                      ])
                    ], 8, _hoisted_23),
                    _createElementVNode("div", _hoisted_31, [
                      (!notif.read)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            onClick: ($event: any) => (handleMarkAsReadOnly(notif, $event)),
                            class: "app-notification-panel__action-btn app-notification-panel__action-btn--check",
                            title: _ctx.$t('notifications.markAsRead')
                          }, [...(_cache[9] || (_cache[9] = [
                            _createElementVNode("i", { class: "fas fa-check" }, null, -1)
                          ]))], 8, _hoisted_32))
                        : _createCommentVNode("", true),
                      _createElementVNode("button", {
                        onClick: ($event: any) => (handleDeleteNotification(notif, $event)),
                        class: "app-notification-panel__action-btn app-notification-panel__action-btn--delete",
                        title: _ctx.$t('notifications.delete')
                      }, [...(_cache[10] || (_cache[10] = [
                        _createElementVNode("i", { class: "fas fa-trash-alt" }, null, -1)
                      ]))], 8, _hoisted_33)
                    ])
                  ], 6))
                }), 128))
              ]))
    ])
  ]))
}
}

})